/** Graphics quality settings for the 3D city (rendering-layer only; never touches the sim/save).
 *  Persisted to its own localStorage key. CityCanvas reads the resolved preset and, for 'auto',
 *  adapts the render scale each second to hold a target frame rate. */
import { writable } from 'svelte/store';

export type GfxQuality = 'auto' | 'low' | 'medium' | 'high';

export interface GfxPreset {
  label: string;
  renderScale: number; // multiplied into devicePixelRatio (capped by it)
  msaa: number; // composer MSAA samples (0 / 2 / 4)
  gtao: boolean; // ambient-occlusion pass
  smaa: boolean; // post AA pass
  adaptive: boolean; // 'auto' → tune renderScale to the FPS target below
}

// Ordered for the settings UI.
export const GFX_ORDER: GfxQuality[] = ['auto', 'low', 'medium', 'high'];

export const PRESETS: Record<GfxQuality, GfxPreset> = {
  auto: { label: 'Auto', renderScale: 1.0, msaa: 2, gtao: true, smaa: true, adaptive: true },
  low: { label: 'Low', renderScale: 0.8, msaa: 0, gtao: false, smaa: true, adaptive: false },
  medium: { label: 'Medium', renderScale: 1.0, msaa: 2, gtao: true, smaa: true, adaptive: false },
  high: { label: 'High', renderScale: 1.25, msaa: 2, gtao: true, smaa: true, adaptive: false },
};

// Auto floats the render scale within [MIN, MAX] to keep FPS inside this band (only resolution
// changes — no shader recompiles — so adaptation is smooth).
export const AUTO_TARGET_LOW = 48;
export const AUTO_TARGET_HIGH = 58;
export const AUTO_MIN_SCALE = 0.62;
export const AUTO_MAX_SCALE = 1.25;

const KEY = 'cloud-nine-gfx';

function load(): GfxQuality {
  try {
    const v = localStorage.getItem(KEY) as GfxQuality | null;
    if (v && PRESETS[v]) return v;
  } catch {
    /* no storage */
  }
  return 'auto';
}

export const gfxQuality = writable<GfxQuality>(load());
gfxQuality.subscribe((v) => {
  try {
    localStorage.setItem(KEY, v);
  } catch {
    /* no storage */
  }
});
